import { useFlows, useUpdateFlowDisableState } from '@api/flows';
import { DataGrid } from '@components/DataGrid';
import { ApprovalDialog } from '@components/Forms';
import { Typography } from '@mui/material';
import { Box, styled } from '@mui/system';
import { GridRowParams } from '@mui/x-data-grid';
import { getFlowsColumns } from '@utils/tables';
import { FLOWS_FIELDS } from 'constants/global';
import { APP_ROUTES } from 'constants/routes';
import { useToast } from 'hooks';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IFlow } from 'types/flows';

const VISIBILITY_MODEL = {
	[FLOWS_FIELDS.version]: false,
	[FLOWS_FIELDS.createdTime]: false
};

const initialActionState: {
	open: boolean;
	flow?: IFlow;
	action?: 'activate' | 'disable';
} = {
	open: false,
	flow: undefined,
	action: undefined
};

const FlowsDataGrid = styled(DataGrid)`
	.flows-table-row {
		cursor: pointer;
	}
`;

const FlowsTable: React.FC = () => {
	const { invokeToastableAction } = useToast();
	const { data, isLoading } = useFlows();
	const navigate = useNavigate();
	const disableStateMutation = useUpdateFlowDisableState();
	const [actionConfirmation, setActionConfirmation] =
		React.useState(initialActionState);

	const onEditFlowClicked = React.useCallback(
		(params: GridRowParams) => {
			navigate(`${APP_ROUTES.flowsBuilder}/${params.id}`);
		},
		[navigate]
	);

	const onDisableActionClicked = React.useCallback(
		(flow: IFlow) => {
			setActionConfirmation({
				open: true,
				flow,
				action: 'disable'
			});
		},
		[setActionConfirmation]
	);

	const onActivateActionClicked = React.useCallback(
		(flow: IFlow) => {
			setActionConfirmation({
				open: true,
				flow,
				action: 'activate'
			});
		},
		[setActionConfirmation]
	);

	const flowColumns = React.useMemo(
		() => getFlowsColumns(onDisableActionClicked, onActivateActionClicked),
		[onDisableActionClicked, onActivateActionClicked]
	);

	const onDisableStatusConfirmed = React.useCallback(async () => {
		await invokeToastableAction(
			() =>
				disableStateMutation.mutateAsync({
					disable: !actionConfirmation.flow?.disabled,
					flowId: actionConfirmation.flow?.id!
				}),
			`flows.${actionConfirmation.action}.toast`,
			() => setActionConfirmation(initialActionState)
		);
	}, [
		setActionConfirmation,
		actionConfirmation,
		disableStateMutation,
		invokeToastableAction
	]);

	const handleCancelConfirmation = React.useCallback(() => {
		setActionConfirmation(initialActionState);
	}, [setActionConfirmation]);

	return (
		<Box display="flex" flexGrow={1} data-testid="flows-table">
			{actionConfirmation.open && (
				<ApprovalDialog
					onApprove={onDisableStatusConfirmed}
					onCancel={handleCancelConfirmation}
					title={intl.tr(`flows.${actionConfirmation.action}.dialog.title`)}
					variant="contained"
					color="primary"
					question={
						<Box>
							<Typography>
								{intl.tr(`flows.${actionConfirmation.action}.dialog.question`, {
									name: actionConfirmation.flow?.name
								})}
							</Typography>
							<Typography>
								{intl.tr(`flows.${actionConfirmation.action}.dialog.warning`)}
							</Typography>
						</Box>
					}
					approveText={intl.tr(
						`flows.column.action.${actionConfirmation.action}.button`
					)}
					cancelText={intl.tr('button.cancel')}
				/>
			)}
			<FlowsDataGrid
				noDataMessageId="grid.data.no.data.flows"
				columnVisibilityModel={VISIBILITY_MODEL}
				rows={data?.flows || []}
				columns={flowColumns}
				loading={isLoading}
				getRowClassName={() => 'flows-table-row'}
				onRowClick={onEditFlowClicked}
				checkboxSelection={false}
				autoHeight
				initialState={{
					sorting: {
						sortModel: [{ field: FLOWS_FIELDS.id, sort: 'desc' }]
					}
				}}
			/>
		</Box>
	);
};

export default FlowsTable;
